// src/auth/AuthContext.jsx
import React, { useContext, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthState, saveAuth, saveTenant, saveTenants, saveCustomers, clearAuthStorage, loadAuthStorage } from './useAuthState.js';
import { registerAuthCallbacks } from './apiFetch.js';
import { SA_PURGED_BANNER_KEY, clearAdminLastTenantKeys, reportIfTenantPurged } from './tenantPurged.js';
import { clearPermissionsDrift, notePermissionsDrift, DRIFT_GRANTED } from './permissionsDrift.js';
import { PermissionsDriftBanner } from './PermissionsDriftNotice.jsx';
import { clearPendingInvitation, hasPendingInvitation } from './pendingInvitation.js';
import { API_V1, getSessionHeaders } from '../services/api/config.js';
import i18n from '../i18n/index.js';
import { buildApiError } from '../context/parseApiError.js';
import { loginErrorCopy } from './authErrorCopy.js';
import { seedLocaleFromUser } from '../i18n/locale.js';
import { AuthContext } from './authContextObject.js';


export function AuthProvider({ children }) {
  const { state, dispatch } = useAuthState();
  const navigate = useNavigate();

  // Register global 401/403 callbacks so apiFetch can trigger navigation
  useEffect(() => {
    registerAuthCallbacks({
      onSessionExpired: () => {
        dispatch({ type: 'SESSION_EXPIRED' });
        navigate('/login', { replace: true });
      },
      onTenantDenied: () => {
        dispatch({ type: 'TENANT_DENIED' });
        navigate('/select-tenant', { replace: true });
      },
      onSuperadminTenantBlocked: () => {
        try { sessionStorage.setItem('sa_blocked_banner', '1'); } catch { /* private mode */ }
        navigate('/admin/users', { replace: true });
      },
      // 410 TENANT_PURGED (auth/tenantPurged.js has already dropped the workspace from storage). In admin mode a
      // superadmin was looking at that tenant in the console: forget the pages' stored "last tenant" and land on
      // the tenant list with a one-shot banner. Otherwise only the ACTIVE workspace re-routes: the picker if any
      // workspace is left (never auto-select), admin mode for a superadmin with none, /no-access for a member.
      onTenantPurged: (tenantId, { wasActive } = {}) => {
        dispatch({ type: 'TENANT_PURGED', tenantId, wasActive });
        if (state.adminMode) {
          clearAdminLastTenantKeys(tenantId);
          try { sessionStorage.setItem(SA_PURGED_BANNER_KEY, '1'); } catch { /* private mode */ }
          navigate('/admin/tenants', { replace: true });
          return;
        }
        if (!wasActive) return;
        const remaining = loadAuthStorage().tenants || [];
        if (remaining.length > 0) {
          navigate('/select-tenant', { replace: true });
        } else if (state.isSuperadmin) {
          dispatch({ type: 'SET_ADMIN_MODE', value: true });
          navigate('/admin/users', { replace: true });
        } else {
          navigate('/no-access', { replace: true });
        }
      },
      getIsSuperadmin: () => state.isSuperadmin,
      getAdminMode: () => state.adminMode,
    });
  }, [dispatch, navigate, state.isSuperadmin, state.adminMode]);

  const lastMeRefreshRef = useRef(0);

  // ── applySuperadminChange — platform rights and the routing they imply ─
  // Extracted so BOTH sources of `is_superadmin` can share it:
  //   • a whole new session (applyClaims, from /login or an invitation accept);
  //   • the focus re-sync's GET /auth/me, which reads `is_superadmin` from the
  //     users row and is therefore DB-fresh even though its two claim arrays are
  //     not — so a grant or a revocation made mid-session IS picked up.
  //
  // `tenants` is consulted for one thing only: where a demoted superadmin lands.
  // The caller therefore passes the list IT trusts — the payload's for a new
  // session, the live state's for /me (⛔ /me's `tenants` is the caller's own
  // token replayed and must never reach the claims logic).
  //
  // Returns true when it navigated, so a caller can skip a second navigation.
  const applySuperadminChange = useCallback((next, tenants) => {
    if (next === state.isSuperadmin) return false;
    dispatch({ type: 'SET_SUPERADMIN', value: next });
    if (state.isSuperadmin && !next) {
      // true → false: route out of admin mode
      dispatch({ type: 'SET_ADMIN_MODE', value: false });
      navigate((tenants || []).length === 0 ? '/no-access' : '/select-tenant', { replace: true });
      return true;
    }
    // false → true: route into admin mode
    dispatch({ type: 'SET_ADMIN_MODE', value: true });
    navigate('/admin/users', { replace: true });
    return true;
  }, [state.isSuperadmin, dispatch, navigate]);

  // ── applyClaims — the ONE place login-bound claims are adopted ────────
  // Takes a LoginResponse-shaped payload ({is_superadmin, tenants, customers})
  // and reconciles it with the current state: superadmin grant/revoke (with the
  // admin-mode re-route), the customer list, the tenant list, and the bounce
  // when the active tenant is gone.
  //
  // ⚠️ Feed this ONLY from a payload whose `tenants` / `customers` were rebuilt
  // from the DB: a /login response, or the re-minted session an invitation accept
  // hands back (applySession). `GET /auth/me` does NOT qualify — it rebuilds
  // those two arrays from the caller's own JWT claims, so passing /me's payload
  // here would compare the token against itself and report stale data as fresh.
  // /me is still the source for the profile fields, `is_active` and
  // `is_superadmin`, which it reads from the users row.
  const applyClaims = useCallback((body) => {
    const next = !!body.is_superadmin;
    const navigated = applySuperadminChange(next, body.tenants || []);

    const oldCustomers = state.customers || [];
    const newCustomers = body.customers || [];
    const customersDiverged = (
      newCustomers.length !== oldCustomers.length
      || newCustomers.some(nc => {
        const oc = oldCustomers.find(o => o.customer_id === nc.customer_id);
        return !oc || oc.role !== nc.role;
      })
    );
    if (customersDiverged) {
      saveCustomers(newCustomers);
      dispatch({ type: 'CUSTOMERS_REFRESHED', customers: newCustomers });
    }

    const oldTenants = state.tenants || [];
    const newTenants = body.tenants || [];
    const tenantsDiverged = (
      newTenants.length !== oldTenants.length
      || newTenants.some(nt => {
        const ot = oldTenants.find(o => o.tenant_id === nt.tenant_id);
        return !ot || ot.role !== nt.role;
      })
    );
    if (tenantsDiverged) {
      saveTenants(newTenants);
      dispatch({ type: 'TENANTS_REFRESHED', tenants: newTenants });

      if (
        !navigated
        && state.activeTenant
        && !newTenants.find(t => t.tenant_id === state.activeTenant.tenant_id)
      ) {
        // Active tenant removed from the claims we have just adopted.
        localStorage.removeItem('auth_tenant_id');
        if (newTenants.length === 0 && !next) {
          navigate('/no-access', { replace: true });
        } else if (newTenants.length > 0) {
          navigate('/select-tenant', { replace: true });
        }
        // SAs with zero tenants are already handled by the SA-change branch above.
      }
    }
  }, [state.tenants, state.customers, state.activeTenant, dispatch, navigate, applySuperadminChange]);

  // ── applySession — adopt a session handed back by a non-login endpoint ─
  // Standalone by design: it takes a payload that already carries a re-minted
  // `access_token` + `tenants` + `customers` and makes it the live session, with
  // no request of its own. The caller is the invitation-accept page, whose 200
  // body is exactly that shape.
  //
  // ⚠️ The accept response carries NO `is_superadmin`. `applyClaims` reads a
  // missing one as `false`, i.e. as a REVOCATION — which would drop a superadmin
  // out of admin mode and navigate them away mid-accept. So the current value is
  // carried forward explicitly: accepting an invitation says nothing about
  // platform rights, and the only two things entitled to change them are /login
  // and the focus re-sync's GET /auth/me (which reads the users row).
  //
  // ⚠️ Accepted edge: if the user's active workspace was removed since sign-in,
  // the claims arriving here are DB-fresh and `applyClaims` bounces to
  // /select-tenant. That is correct — the workspace really is gone — even though
  // it interrupts the accept page's own success panel.
  const applySession = useCallback((data) => {
    if (!data?.access_token) return;
    // Persist the new token FIRST: applyClaims can navigate, and anything that
    // fires afterwards must already be using it.
    saveAuth({ token: data.access_token, tenants: data.tenants || [], customers: data.customers || [] });
    dispatch({ type: 'TOKEN_REFRESHED', token: data.access_token });
    applyClaims({ ...data, is_superadmin: data.is_superadmin ?? state.isSuperadmin });
  }, [state.isSuperadmin, dispatch, applyClaims]);

  // ── refreshMe — the focus / visibility re-sync (GET /auth/me) ─────────
  // Throttled to one call per 30 s. It refreshes exactly what /me reads from the
  // users row: the profile (name, locale, last_login_at), `is_active`, and
  // `is_superadmin` — so a superadmin grant or revocation made while the user is
  // signed in IS picked up here, through applySuperadminChange.
  //
  // ⛔ There is NO claims-refresh route on the backend: `tenants[]` /
  // `customers[]` are minted at POST /auth/login and nowhere else (an invitation
  // accept, which hands back a whole new session, is the one exception — see
  // applySession). A membership or customer-role change therefore becomes visible
  // only at the next sign-in, which is what the permissions-drift notice asks
  // for. Nothing here can close that gap, and nothing here should pretend to.
  const refreshMe = useCallback(async () => {
    if (!state.token) return;
    const now = Date.now();
    if (now - lastMeRefreshRef.current < 30_000) return;
    lastMeRefreshRef.current = now;

    let body;
    try {
      const res = await fetch(`${API_V1}/auth/me`, {
        headers: { Authorization: `Bearer ${loadAuthStorage().token || state.token}`, ...getSessionHeaders() },
      });
      if (!res.ok) return;
      body = await res.json();
    } catch {
      return;
    }

    // Keep the local profile (name / locale / last_login_at) in sync with the DB
    // so edits made in another tab, or a fresh last_login_at, propagate on refocus.
    const prof = state.user || {};
    if (
      prof.first_name !== body.first_name
      || prof.last_name !== body.last_name
      || prof.locale !== body.locale
      || prof.last_login_at !== body.last_login_at
    ) {
      dispatch({ type: 'SET_USER', user: { ...prof, ...body } });
    }
    // ⭐ Account → UI, for a browser that has made no choice of its own.
    seedLocaleFromUser(body.locale);

    // `is_superadmin` comes from the users row, so it IS DB-fresh: apply it with
    // the same logic a new session uses. The tenant list handed to the helper is
    // the LIVE one, never the payload's (see below).
    applySuperadminChange(!!body.is_superadmin, state.tenants);

    // ⛔ NOTHING about tenants/customers is read from this payload: /me rebuilds
    // those two arrays from the caller's own JWT, so they are the token replayed
    // at us. Feeding them into the claims logic would compare the token against
    // itself and report stale data as fresh.
  }, [state.token, state.user, state.tenants, dispatch, applySuperadminChange]);

  useEffect(() => {
    function onVisibility() {
      if (document.visibilityState === 'visible') refreshMe();
    }
    window.addEventListener('focus', refreshMe);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', refreshMe);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refreshMe]);

  // ── login ────────────────────────────────────────────────────────────
  const login = useCallback(async (email, password) => {
    // An invitation link parked its token before sending the user here (the
    // accept endpoint needs a bearer). Whatever this sign-in would otherwise
    // land on, the invitation comes first: it is the reason the user is here,
    // it is single-use, and the accept page is where every outcome is worded.
    // The four destinations below stay exactly as they were — each goes through
    // go(), which only substitutes the accept page when a token is waiting.
    const go = (defaultPath) => {
      const to = hasPendingInvitation() ? '/invitations/accept' : defaultPath;
      navigate(to, { replace: true });
    };

    dispatch({ type: 'CLEAR_ERROR' });
    // A sign-in re-mints the claims, which is exactly what a drift asked for.
    clearPermissionsDrift();
    let res;
    try {
      res = await fetch(`${API_V1}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getSessionHeaders(),
        },
        body: JSON.stringify({ email, password }),
      });
    } catch {
      dispatch({ type: 'SET_ERROR', error: i18n.t('errors:auth.network') });
      return;
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      // ⛔ Never the server's `message`: every refusal is worded from its code by
      // authErrorCopy.js (an unmapped code is printed, a 5xx quotes request_id).
      dispatch({ type: 'SET_ERROR', error: loginErrorCopy(i18n.t.bind(i18n), buildApiError(body, res.status)) });
      return;
    }

    const data = await res.json();
    const { access_token, tenants, is_superadmin } = data;
    // Customer memberships are minted into the JWT here, at login, and nowhere else.
    // Absent on an old backend → `[]` → no customer-admin rights (the safe default).
    const loginCustomers = data.customers || [];

    if (!tenants || tenants.length === 0) {
      if (is_superadmin) {
        // Pure superadmin: zero user_tenants rows is the EXPECTED state per BE handoff.
        saveAuth({ token: access_token, tenants: [], customers: loginCustomers });
        const userPayload = { email };
        try {
          const meRes = await fetch(`${API_V1}/auth/me`, {
            headers: { Authorization: `Bearer ${access_token}`, ...getSessionHeaders() },
          });
          if (meRes.ok) Object.assign(userPayload, await meRes.json());
        } catch { /* non-fatal */ }
        // ⭐ First login on a new machine honours the account language at once.
        seedLocaleFromUser(userPayload.locale);
        dispatch({ type: 'LOGIN_SUCCESS', phase: 'ready', user: userPayload, token: access_token, tenants: [], customers: userPayload.customers || loginCustomers });
        dispatch({ type: 'SET_SUPERADMIN', value: true });
        dispatch({ type: 'SET_ADMIN_MODE', value: true });
        go('/admin/users');
        return;
      }
      saveAuth({ token: access_token, tenants: [], customers: loginCustomers });
      dispatch({ type: 'LOGIN_SUCCESS', phase: 'no_tenants', user: null, token: access_token, tenants: [], customers: loginCustomers });
      dispatch({ type: 'SET_SUPERADMIN', value: false });
      go('/no-access');
      return;
    }

    saveAuth({ token: access_token, tenants, customers: loginCustomers });

    // Fetch user info from /me (includes is_superadmin fresh from DB)
    let user = { email };
    try {
      const meRes = await fetch(`${API_V1}/auth/me`, { headers: { Authorization: `Bearer ${access_token}`, ...getSessionHeaders() } });
      if (meRes.ok) user = await meRes.json();
    } catch { /* non-fatal */ }
    // ⭐ First login on a new machine honours the account language at once.
    seedLocaleFromUser(user.locale);

    const isSuperadmin = !!(user.is_superadmin ?? is_superadmin);
    // /me replays the same JWT claims, so this is the login value either way.
    const customers = user.customers || loginCustomers;
    if (customers !== loginCustomers) saveCustomers(customers);

    if (tenants.length === 1) {
      // Auto-select single tenant
      saveTenant(tenants[0].tenant_id);
      let applications = [];
      try {
        const appRes = await fetch(`${API_V1}/user-config/tenants/${tenants[0].tenant_id}/applications`, {
          headers: { Authorization: `Bearer ${access_token}`, ...getSessionHeaders() },
        });
        if (appRes.ok) applications = await appRes.json();
      } catch { /* non-fatal */ }

      dispatch({ type: 'LOGIN_SUCCESS', phase: 'ready', user, token: access_token, tenants, customers });
      dispatch({ type: 'SET_SUPERADMIN', value: isSuperadmin });
      dispatch({ type: 'TENANT_SELECTED', activeTenant: tenants[0], applications });
      go('/');
    } else {
      dispatch({ type: 'LOGIN_SUCCESS', phase: 'tenant_pick', user, token: access_token, tenants, customers });
      dispatch({ type: 'SET_SUPERADMIN', value: isSuperadmin });
      go('/select-tenant');
    }
  }, [dispatch, navigate]);

  // ── selectTenant ─────────────────────────────────────────────────────
  // Takes either a tenant object or a tenant id. TenantPicker passes an id and
  // keeps working unchanged.
  //
  // ⚠️ Why an OBJECT is accepted at all: an id is resolved against the tenant
  // list, and this callback closes over `state.tenants`. A caller that has just
  // adopted a new session (applySession, after accepting an invitation) holds a
  // tenant this closure has never seen — the render that recreates it has not
  // happened yet — so an id-only API would silently do nothing on exactly the
  // workspace the user was invited to. `loadAuthStorage()` is consulted as the
  // second source for the same reason (it was written by applySession before the
  // dispatch), and passing the object skips the question entirely.
  const selectTenant = useCallback(async (tenantOrId, { to = '/' } = {}) => {
    const tenant = (tenantOrId && typeof tenantOrId === 'object')
      ? tenantOrId
      : (
        state.tenants.find(t => t.tenant_id === tenantOrId)
        || (loadAuthStorage().tenants || []).find(t => t.tenant_id === tenantOrId)
      );
    if (!tenant) return;
    const tenantId = tenant.tenant_id;
    saveTenant(tenantId);

    const token = loadAuthStorage().token;
    let applications = [];
    let purged = false;
    try {
      const res = await fetch(`${API_V1}/user-config/tenants/${tenantId}/applications`, {
        headers: { Authorization: `Bearer ${token}`, ...getSessionHeaders() },
      });
      // 410 TENANT_PURGED: the handler drops it (it was just saved as active) and re-routes — select nothing.
      purged = await reportIfTenantPurged(res, tenantId);
      if (!purged && res.ok) applications = await res.json();
    } catch { /* non-fatal */ }
    if (purged) return;

    dispatch({ type: 'TENANT_SELECTED', activeTenant: tenant, applications });
    navigate(to, { replace: true });
  }, [state.tenants, dispatch, navigate]);

  // ── logout ────────────────────────────────────────────────────────────
  // `opts.notice` is a key LoginScreen words as a one-shot banner.
  //
  // ⚠️ It is read defensively because this is also an onClick handler:
  // NoAccessScreen passes `logout` straight to a button, so the argument that
  // actually arrives is a click event. Anything that is not a string is "no
  // notice".
  //
  // ⭐ A PLAIN sign-out CLEARS the parked invitation token: the user chose to
  // leave, and a token surviving that would hijack their next sign-in into an
  // accept page they never asked for. Only a sign-out we perform in SERVICE of
  // an invitation (`logout({ notice: 'invitation' })` — "sign in with the
  // invited address") keeps it, because resuming it is the whole point.
  const logout = useCallback((opts) => {
    const notice = typeof opts?.notice === 'string' ? opts.notice : null;
    clearPermissionsDrift();
    clearAuthStorage();
    if (!notice) clearPendingInvitation();
    dispatch({ type: 'LOGOUT' });
    navigate('/login', { replace: true, state: notice ? { notice } : null });
  }, [dispatch, navigate]);

  const setAdminMode = useCallback((value) => {
    dispatch({ type: 'SET_ADMIN_MODE', value });
  }, [dispatch]);

  // Replace the local user object (e.g. after a self-service profile PATCH).
  const setUser = useCallback((user) => {
    dispatch({ type: 'SET_USER', user });
  }, [dispatch]);

  // ── Re-authentication — the ONE swap point ────────────────────────────
  // The action behind every permissions-drift affordance. Callers pass no
  // arguments and render no copy of their own, so the banner, the inline notice
  // and their strings are untouched by whatever happens in here — keep it that
  // way for whatever replaces it next.
  //
  // It signs the user out, because that is the only honest remedy: the backend
  // mints `tenants[]` / `customers[]` at POST /auth/login and serves no route
  // that re-mints them, so a fresh sign-in is what fixes a drift. The copy
  // ("Accedi di nuovo") already says exactly that.
  // ⛔ Never implement this as a /auth/me refetch — /me replays the same JWT
  // claims and would report the stale state back as though it were fresh.
  const reauthenticate = useCallback(() => {
    clearPermissionsDrift();
    clearAuthStorage();
    dispatch({ type: 'LOGOUT' });
    navigate('/login', { replace: true });
  }, [dispatch, navigate]);

  // The inverse direction of the same defect: a right was granted elsewhere and
  // cannot appear in this session because the claims are login-bound. Surfaces
  // that stay hidden for that reason call this; it renders the SAME message as a
  // refusal-driven drift, because it is the same problem and the same remedy.
  const notePermissionsGranted = useCallback(() => {
    notePermissionsDrift(DRIFT_GRANTED);
  }, []);

  // Workspaces this user may administer from Gestione Workspace — ANY entry with
  // role 'admin' (real memberships AND the implicit ones a company admin gets on
  // every ACTIVE workspace of the company), not just the active one.
  const adminTenants = useMemo(
    () => (state.tenants || []).filter(t => t.role === 'admin'),
    [state.tenants],
  );

  // ⚠️ The ONE place tenant-admin rights are derived — every consumer
  // (RequireTenantAdmin, Sidebar, the KB/context `canWrite` gates) reads this.
  // Since the BE change of 2026-08-26 it is true in TWO cases, and the claims do
  // not distinguish them (`via` is stripped from LoginResponse/MeResponse):
  //   1. a real `user_tenants` row with role='admin' in the active tenant;
  //   2. the caller is admin of the CUSTOMER that owns the active tenant — the
  //      claim builder mints an implicit `role:'admin'` entry for every tenant
  //      that customer owns, and the dedupe is privilege-aware (strongest role
  //      wins per tenant, so an `editor` membership no longer shadows it).
  // Case 2 is INTENDED, not a leak: a customer-admin is the higher authority over
  // the workspaces its customer owns and gets the whole /tenant-admin/* surface.
  // ⛔ Therefore `true` here does NOT prove a membership row exists — never build
  // UI on "admin implies someone explicitly made me admin of this workspace"
  // (e.g. a removable/leave affordance). The tenant-admin user-listing endpoint
  // exposes `via` ('member' | 'customer') + `removable` per row for that.
  const isTenantAdmin = state.activeTenant?.role === 'admin';

  // ── Customer-admin ────────────────────────────────────────────────────
  // ⚠️ A CUSTOMER admin (billing entity: buys plans, owns the wallet) is a
  // DIFFERENT thing from a tenant/workspace admin, and the implication runs one
  // way only: `tenants[].role === 'admin'` says NOTHING about customer rights
  // (case 1 above), while a customer-admin DOES now imply tenant-admin on every
  // workspace their customer owns (case 2 above). Gate customer rights on
  // `customers[]` — never infer them from a tenant role.
  //
  // ⚠️ CLAIM-BOUND, and the backend serves no route that re-mints the claims.
  // `state.customers` is minted into the JWT at POST /auth/login; the only other
  // thing that can change it is an invitation accept, which hands back a whole
  // new session (applySession). So a PB5 grant or revocation made elsewhere stays
  // invisible until the next sign-in — which is exactly what the
  // permissions-drift notice asks the user to do.
  // ⛔ It is NOT refreshable via /auth/me: that endpoint rebuilds its response
  // from the caller's own claims and would report the stale answer back as though
  // it were fresh. Never add a "refresh permissions" control on /me.
  // ⚠️ So the flag can be stale for the whole session, and consumers must keep
  // handling a 403 CUSTOMER_ADMIN_REQUIRED even when this returns true — the
  // server is the authoritative gate.
  //
  // `customers` entries are { customer_id, role, legal_name }; role ∈ 'admin' | 'viewer'.
  // ⛔ Gate on the ROLE, never on the array being non-empty — a `viewer` is returned
  // honestly and grants nothing. Absent `customers` (old backend) → [] → false.

  // Authoritative form: "is this user an admin of THIS customer?". The id comes from
  // GET /credits/balance's `customer_id`, which resolves asynchronously — call this
  // once you hold it. A null/undefined id yields false (no id, no right).
  const customers = state.customers;
  const isCustomerAdminOf = useCallback((customerId) => {
    if (!customerId) return false;
    return (customers || []).some(c => c.customer_id === customerId && c.role === 'admin');
  }, [customers]);

  // Coarse pre-gate: "does this user administer ANY customer?". Use it only to keep a
  // surface hidden before `customer_id` is known; once it is, confirm with
  // isCustomerAdminOf(customerId) — being admin of a DIFFERENT customer grants nothing.
  const isCustomerAdmin = (state.customers || []).some(c => c.role === 'admin');

  // ⛔ Not isTenantAdmin (active workspace only): an admin on A whose active
  // workspace is B, or a company admin active on another company's workspace,
  // must still reach the page. Superadmins use /admin/*.
  // ⛔ This is a NEW, SEPARATE flag — never a redefinition of isTenantAdmin, which
  // Knowledge Base / personas / runs still gate on (and which
  // must stay "the ACTIVE workspace", because those pages act on that one alone).
  const canManageWorkspaces = !state.isSuperadmin && (adminTenants.length > 0 || isCustomerAdmin);

  // Patch a workspace's display name locally, right after a successful rename.
  // Writes the persisted copy too, so the new name survives a re-render and a
  // tenant re-selection. ⚠️ It does NOT survive a reload: boot takes `tenants`
  // from /auth/me, which replays the token's claims, so the old name comes back
  // until the next sign-in — accepted, and the rename copy says so.
  const renameTenantLocally = useCallback((tenantId, displayName) => {
    const next = (state.tenants || []).map(t => (t.tenant_id === tenantId ? { ...t, display_name: displayName } : t));
    saveTenants(next);
    dispatch({ type: 'TENANT_RENAMED', tenant_id: tenantId, display_name: displayName });
  }, [state.tenants, dispatch]);

  const value = {
    ...state,
    isTenantAdmin,
    adminTenants,
    canManageWorkspaces,
    renameTenantLocally,
    isCustomerAdmin,
    isCustomerAdminOf,
    reauthenticate,
    notePermissionsGranted,
    applySession,
    login,
    selectTenant,
    logout,
    setAdminMode,
    setUser,
  };

  // The banner is mounted HERE, not in AppShell: a 403 TENANT_ACCESS_DENIED
  // bounces the user to the tenant picker, which lives outside the shell — a
  // shell-level banner would unmount at the exact moment it is needed.
  return (
    <AuthContext.Provider value={value}>
      <PermissionsDriftBanner />
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
